import { supabase } from './supabase';
import type { LocalCity, LocalService, LocalPage, LocalTemplate } from './localPages';

/**
 * Public readers for the Local Service Pages system. Every query runs on the
 * anonymous client and is bounded by RLS: only `published` pages, `active`
 * templates, `running` experiments and `approved`/`active` cities & `active`
 * services are ever returned. Drafts are invisible here by design.
 */

export type PublicCity = Pick<
  LocalCity,
  'id' | 'name' | 'slug' | 'state' | 'county' | 'metro_region' | 'lat' | 'lng' | 'distance_miles_from_hq' | 'population' | 'facts' | 'status' | 'priority'
>;

export type PublicService = Pick<LocalService, 'id' | 'slug' | 'name' | 'parent_page_path' | 'short_pitch' | 'keywords' | 'position'>;

export type PublicPage = Pick<
  LocalPage,
  'id' | 'city_id' | 'service_id' | 'page_type' | 'path' | 'template_id' | 'content' | 'title' | 'meta_title' | 'meta_description' | 'h1' | 'faq' | 'images' | 'status' | 'quality_score' | 'updated_at' | 'published_at'
>;

/** The template theme (palette + image density) a page inherits. */
export interface PublicTemplateTheme {
  theme: Record<string, unknown>;
}

export interface RunningExperiment {
  id: string;
  name: string;
  scope: 'page' | 'page_type' | 'service';
  scope_value: string | null;
  variants: Array<{ key: string; template_id: string; weight: number }>;
  primary_goal: string;
  min_sample: number;
}

/** Active cities/services for the /locations directory. */
export async function listPublishedCities(): Promise<PublicCity[]> {
  try {
    const { data, error } = await supabase
      .from('local_cities')
      .select('id, name, slug, state, county, metro_region, lat, lng, distance_miles_from_hq, population, facts, status, priority')
      .in('status', ['approved', 'active'])
      .order('name');
    if (error) throw error;
    return (data as PublicCity[]) || [];
  } catch {
    return [];
  }
}

export async function listPublishedServices(): Promise<PublicService[]> {
  try {
    const { data, error } = await supabase
      .from('local_services')
      .select('id, slug, name, parent_page_path, short_pitch, keywords, position')
      .eq('status', 'active')
      .order('position');
    if (error) throw error;
    return (data as PublicService[]) || [];
  } catch {
    return [];
  }
}

export async function getPublishedCityBySlug(slug: string): Promise<PublicCity | null> {
  try {
    const { data, error } = await supabase
      .from('local_cities')
      .select('id, name, slug, state, county, metro_region, lat, lng, distance_miles_from_hq, population, facts, status, priority')
      .eq('slug', slug)
      .in('status', ['approved', 'active'])
      .maybeSingle();
    if (error) throw error;
    return (data as PublicCity) || null;
  } catch {
    return null;
  }
}

export async function getPublishedPageByPath(path: string): Promise<PublicPage | null> {
  try {
    const { data, error } = await supabase
      .from('local_pages')
      .select('id, city_id, service_id, page_type, path, template_id, content, title, meta_title, meta_description, h1, faq, images, status, quality_score, updated_at, published_at')
      .eq('path', path)
      .eq('status', 'published')
      .maybeSingle();
    if (error) throw error;
    return (data as PublicPage) || null;
  } catch {
    return null;
  }
}

/**
 * Admin draft-preview readers — the same shape as the published readers but
 * WITHOUT the status filter. Row Level Security still bounds anonymous callers
 * to `published` rows (the `*_public_read` policies), so a signed-out visitor can
 * never read a draft through these; only a signed-in site admin (the
 * `*_admin_all` policies) sees unpublished pages, cities and services.
 */
export async function getPreviewCityBySlug(slug: string): Promise<PublicCity | null> {
  try {
    const { data, error } = await supabase
      .from('local_cities')
      .select('id, name, slug, state, county, metro_region, lat, lng, distance_miles_from_hq, population, facts, status, priority')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw error;
    return (data as PublicCity) || null;
  } catch {
    return null;
  }
}

export async function listPreviewServices(): Promise<PublicService[]> {
  try {
    const { data, error } = await supabase
      .from('local_services')
      .select('id, slug, name, parent_page_path, short_pitch, keywords, position')
      .order('position');
    if (error) throw error;
    return (data as PublicService[]) || [];
  } catch {
    return [];
  }
}

export async function getPreviewPageByPath(path: string): Promise<PublicPage | null> {
  try {
    const { data, error } = await supabase
      .from('local_pages')
      .select('id, city_id, service_id, page_type, path, template_id, content, title, meta_title, meta_description, h1, faq, images, status, quality_score, updated_at, published_at')
      .eq('path', path)
      .maybeSingle();
    if (error) throw error;
    return (data as PublicPage) || null;
  } catch {
    return null;
  }
}

/** Published city-hub paths (for internal links + nearby-city blocks). */
export async function listPublishedHubPaths(): Promise<Array<{ path: string; city_id: string | null }>> {
  try {
    const { data, error } = await supabase
      .from('local_pages')
      .select('path, city_id')
      .eq('status', 'published')
      .eq('page_type', 'city_hub');
    if (error) throw error;
    return (data as Array<{ path: string; city_id: string | null }>) || [];
  } catch {
    return [];
  }
}

/** Published pages for one city (both hub + service pages). */
export async function listPublishedPagesForCity(cityId: string): Promise<Array<{ path: string; service_id: string | null; page_type: string }>> {
  try {
    const { data, error } = await supabase
      .from('local_pages')
      .select('path, service_id, page_type')
      .eq('status', 'published')
      .eq('city_id', cityId);
    if (error) throw error;
    return (data as Array<{ path: string; service_id: string | null; page_type: string }>) || [];
  } catch {
    return [];
  }
}

/** The active template used to render a page (variant swap source). */
export async function getTemplateBlocks(templateId: string): Promise<{ blocks: LocalTemplate['blocks'] } | null> {
  try {
    const { data, error } = await supabase
      .from('local_templates')
      .select('id, blocks')
      .eq('id', templateId)
      .maybeSingle();
    if (error) throw error;
    return data ? { blocks: (data as { blocks: LocalTemplate['blocks'] }).blocks } : null;
  } catch {
    return null;
  }
}

/**
 * Public reader for a template's theme (palette + image density). Anonymous
 * callers are bounded by the local_templates read policy, so this only ever
 * returns an active template's theme — never a draft.
 */
export async function getPublicTemplateTheme(templateId: string): Promise<PublicTemplateTheme | null> {
  if (!templateId) return null;
  try {
    const { data, error } = await supabase
      .from('local_templates')
      .select('theme')
      .eq('id', templateId)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return { theme: ((data as { theme?: Record<string, unknown> }).theme) || {} };
  } catch {
    return null;
  }
}

/** Running, enabled experiments (RLS already limits to enabled + running). */
export async function listRunningExperiments(): Promise<RunningExperiment[]> {
  try {
    const { data, error } = await supabase
      .from('local_experiments')
      .select('id, name, scope, scope_value, variants, primary_goal, min_sample')
      .eq('enabled', true)
      .eq('status', 'running');
    if (error) throw error;
    return (data as RunningExperiment[]) || [];
  } catch {
    return [];
  }
}

/** The site-wide settings mirrored into the publicly readable row. */
export async function loadLocalPagesPublicSettings(): Promise<{ footerLinkEnabled: boolean; directoryEnabled: boolean; factsEnabled: boolean }> {
  try {
    const { data, error } = await supabase.from('cms_content').select('value').eq('key', 'public_settings').maybeSingle();
    if (error || !data) return { footerLinkEnabled: true, directoryEnabled: true, factsEnabled: true };
    const lp = (data.value as { localPages?: { footerLinkEnabled?: boolean; directoryEnabled?: boolean; factsEnabled?: boolean } })?.localPages;
    return {
      footerLinkEnabled: lp?.footerLinkEnabled !== false,
      directoryEnabled: lp?.directoryEnabled !== false,
      factsEnabled: lp?.factsEnabled !== false,
    };
  } catch {
    return { footerLinkEnabled: true, directoryEnabled: true, factsEnabled: true };
  }
}

/** Great-circle distance in miles between two lat/lng points. */
export function haversineMiles(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 3958.8;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/** A few published Insights articles best matching a service's keywords. */
export async function listRelatedArticles(keywords: string[], limit = 3): Promise<Array<{ title: string; path: string }>> {
  try {
    const { data, error } = await supabase
      .from('articles')
      .select('slug, title, category, tags, date')
      .eq('status', 'published')
      .order('date', { ascending: false })
      .limit(60);
    if (error) throw error;
    const rows = (data || []) as Array<{ slug: string; title: string; category: string | null; tags: string[] | null; date: string | null }>;
    const terms = keywords.map((k) => k.toLowerCase()).filter(Boolean);
    const score = (r: typeof rows[number]) => {
      const hay = `${r.title} ${r.category || ''} ${(r.tags || []).join(' ')}`.toLowerCase();
      return terms.reduce((n, t) => (hay.includes(t) ? n + 1 : n), 0);
    };
    const ranked = rows.slice().sort((a, b) => score(b) - score(a));
    return ranked.slice(0, limit).map((r) => ({ title: r.title, path: `/insights/${r.slug}` }));
  } catch {
    return [];
  }
}